import React, { useState } from "react";
import * as S from "../style";
import { ActionProps, TableHeaderProps } from "../types";
import Svg from "@components/svg";
import styled from "@emotion/styled";
import { ChevronDown, ChevronUp } from "lucide-react";
import Button from "@components/button";

interface TableBodyProps {
  columns: TableHeaderProps[];
  data: any[];
  actions?: ActionProps[];
  expandedRowRender?: (rowData: any, index: number) => React.ReactNode;
  setId?: (val: string) => void;
  onSave?: (data: any) => void;
  hasSaveButton?: boolean;
  onRowClick?: (data: any) => void;
  loading?: boolean;
}

const Body: React.FC<TableBodyProps> = ({
  columns,
  data,
  actions = [],
  expandedRowRender,
  setId,
  onSave,
  hasSaveButton,
  onRowClick,
  loading,
}) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const toggleRow = (index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const isRowExpanded = (index: number) => expandedRows.includes(index);

  return (
    <S.StyleBodyWrapper>
      {loading ? (
        <>
          {Array.from({ length: 8 }).map((_, rowIdx) => (
            <S.StyleTableRow key={`skeleton-${rowIdx}`}>
              {expandedRowRender && (
                <S.StyleBodyText>
                  <SkeletonBox
                    style={{ width: 24, height: 24, borderRadius: 6 }}
                  />
                </S.StyleBodyText>
              )}
              {columns.map((_, colIdx) => (
                <S.StyleBodyText key={`sk-${rowIdx}-${colIdx}`}>
                  {colIdx === 0 ? (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 10 }}
                    >
                      <SkeletonBox
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 12,
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <SkeletonBox
                          style={{
                            width: "70%",
                            height: 14,
                            borderRadius: 6,
                            marginBottom: 6,
                          }}
                        />
                        <SkeletonBox
                          style={{ width: "45%", height: 10, borderRadius: 4 }}
                        />
                      </div>
                    </div>
                  ) : (
                    <SkeletonBox
                      style={{
                        width: `${55 + Math.random() * 35}%`,
                        height: 14,
                        borderRadius: 6,
                      }}
                    />
                  )}
                </S.StyleBodyText>
              ))}
              {actions.length > 0 && (
                <S.StyleBodyText>
                  <div style={{ display: "flex", gap: 6 }}>
                    {actions.map((_, aIdx) => (
                      <SkeletonBox
                        key={aIdx}
                        style={{ width: 28, height: 28, borderRadius: 6 }}
                      />
                    ))}
                  </div>
                </S.StyleBodyText>
              )}
            </S.StyleTableRow>
          ))}
        </>
      ) : data.length > 0 ? (
        <>
          {data.map((item, index) => (
            <React.Fragment key={index}>
              <S.StyleTableRow onClick={() => onRowClick?.(item)}>
                {expandedRowRender && (
                  <S.StyleBodyText
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRow(index);
                      const isExpanding = !isRowExpanded(index);
                      if (isExpanding && setId) setId(item.id);
                    }}
                  >
                    {isRowExpanded(index) ? <ChevronUp /> : <ChevronDown />}
                  </S.StyleBodyText>
                )}

                {columns.map((column, colIndex) => (
                  <S.StyleBodyText key={colIndex}>
                    {column.render
                      ? column.render(item, index + 1)
                      : (item[column.key] ?? "-")}
                  </S.StyleBodyText>
                ))}

                {actions.length > 0 && (
                  <S.StyleBodyText>
                    <S.ActionButtonWrapper>
                      {actions.map((action) => (
                        <S.ActionButton
                          key={action.key}
                          onClick={(e) => action.onClick(item, e)}
                        >
                          {typeof action.icon === "string" ? (
                            <Svg src={action.icon} />
                          ) : (
                            action.icon
                          )}
                        </S.ActionButton>
                      ))}
                    </S.ActionButtonWrapper>
                  </S.StyleBodyText>
                )}
              </S.StyleTableRow>

              {isRowExpanded(index) && expandedRowRender && (
                <StyleExpandedRow>
                  <StyleExpandedCell
                    colSpan={
                      columns.length +
                      (actions.length > 0 ? 1 : 0) +
                      (expandedRowRender ? 1 : 0)
                    }
                  >
                    <>
                      {expandedRowRender(item, index)}
                      {item.hasSaveButton && (
                        <ButtonWrapper>
                          <Button title="Save Details" onClick={onSave} />
                        </ButtonWrapper>
                      )}
                    </>
                  </StyleExpandedCell>
                </StyleExpandedRow>
              )}
            </React.Fragment>
          ))}
        </>
      ) : (
        <S.StyleTableRow>
          <S.StyleBodyText
            colSpan={
              columns.length +
              (actions.length > 0 ? 1 : 0) +
              (expandedRowRender ? 1 : 0)
            }
            style={{ textAlign: "center" }}
          >
            Nothing here
          </S.StyleBodyText>
        </S.StyleTableRow>
      )}
    </S.StyleBodyWrapper>
  );
};

export default Body;

export const StyleExpandedRow = styled.tr``;

export const StyleExpandedCell = styled.td``;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
`;

const shimmer = `
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const SkeletonBox = styled.div`
  ${shimmer}
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 37%, #f0f0f0 63%);
  background-size: 800px 100%;
  animation: shimmer 1.4s ease-in-out infinite;
`;
